import type { ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import { Card } from '../card.tsx';

export interface LedgerColumn<TRow> {
  /** Stable column id, used as the React key for cells. */
  id: string;
  /** Column-header label (rendered in the mono uppercase header row). */
  header: ReactNode;
  /** Cell alignment; `right` is for numeric/metadata cells. */
  align?: 'left' | 'right';
  /** Optional CSS grid track for this column (e.g. `'1fr'`, `'80px'`, `'minmax(0,2fr)'`). */
  width?: string;
  /** Cell renderer for a given row. */
  cell: (row: TRow) => ReactNode;
}

export interface LedgerTableProps<TRow> {
  /** Column definitions (header + per-row cell renderer). */
  columns: LedgerColumn<TRow>[];
  /** The rows to render. */
  rows: TRow[];
  /** Stable React key for a row. */
  getRowKey: (row: TRow) => string;
  /** When provided, rows become keyboard-operable buttons. */
  onRowClick?: (row: TRow) => void;
  className?: string;
  'data-testid'?: string;
}

/**
 * LedgerTable — the prototype `.ledger`: a white `.card` surface carrying a
 * mono uppercase column-header row (10–11px, +0.08em tracking,
 * `--color-stone-mid`) above compact rows separated by `--color-line` hairlines.
 *
 * Columns lay out on a shared CSS grid so header labels stay aligned with their
 * cells. Right-aligned cells use `--font-mono` for numeric/metadata values.
 * When `onRowClick` is set, each row is a keyboard-accessible button
 * (role=button, Enter/Space) with a subtle `--color-line-2` hover.
 */
export function LedgerTable<TRow>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  className,
  'data-testid': testId,
}: LedgerTableProps<TRow>) {
  const gridTemplateColumns = columns.map((c) => c.width ?? '1fr').join(' ');
  const clickable = Boolean(onRowClick);

  return (
    <Card data-testid={testId} className={cn('overflow-hidden', className)}>
      {/* Column-header row: mono, uppercase, faint, on the line-2 hairline. */}
      <div
        className="grid items-center gap-3.5 border-b border-[var(--color-line-2)] px-6 py-2.5"
        style={{ gridTemplateColumns }}
      >
        {columns.map((col) => (
          <div
            key={col.id}
            className={cn(
              'font-[var(--font-mono)] text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--color-stone-mid)] whitespace-nowrap',
              col.align === 'right' && 'text-right',
            )}
          >
            {col.header}
          </div>
        ))}
      </div>

      {/* Rows: hairline-separated, compact, optionally clickable. */}
      <div className="flex flex-col">
        {rows.map((row) => {
          const key = getRowKey(row);
          const cells = columns.map((col) => (
            <div
              key={col.id}
              className={cn(
                'min-w-0 truncate text-[13px] text-[var(--color-charcoal)]',
                col.align === 'right' &&
                  'text-right font-[var(--font-mono)] text-[var(--color-stone-700)]',
              )}
            >
              {col.cell(row)}
            </div>
          ));

          const sharedClassName = cn(
            'grid items-center gap-3.5 border-t border-[var(--color-line)] px-6 py-2.5 text-left first:border-t-0',
          );

          if (clickable) {
            return (
              // biome-ignore lint/a11y/useSemanticElements: full button semantics are implemented (role, tabIndex, Enter/Space); a native <button> would break the pixel-audited CSS-grid row layout.
              <div
                key={key}
                role="button"
                tabIndex={0}
                data-testid={testId ? `${testId}-row-${key}` : undefined}
                onClick={() => onRowClick?.(row)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick?.(row);
                  }
                }}
                className={cn(
                  sharedClassName,
                  // Rows are flush + full-width (prototype .lrow): the hover fills
                  // the row with no per-row rounding, which clipped oddly against
                  // the full-width hairlines + the card's rounded corners.
                  'cursor-pointer transition-colors hover:bg-[var(--color-line-2)]',
                  'focus-visible:outline-none focus-visible:bg-[var(--color-line-2)] focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-[var(--color-ring)]',
                )}
                style={{ gridTemplateColumns }}
              >
                {cells}
              </div>
            );
          }

          return (
            <div
              key={key}
              data-testid={testId ? `${testId}-row-${key}` : undefined}
              className={sharedClassName}
              style={{ gridTemplateColumns }}
            >
              {cells}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
